import { Box, Paper, Stack, Typography } from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import {
  BrokenImage as BrokenImageIcon,
  Rotate90DegreesCw as RotateIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
} from '@mui/icons-material'
import { darkGrey } from '$constants'

type ImageData = {
  width: number
  height: number
  canvasWidth: number
  canvasHeight: number
  image: HTMLImageElement
  rotation: number
  dx: number
  dy: number
}

export type ImageViewerProps = {
  fileUri: string
  width: number
  height: number
}

/**
 * A component to display an image file in a canvas element, allowing for zooming
 * ,panning, and rotating the image.
 * @param param0
 * @returns
 */
export const ImageViewer = ({ fileUri, width, height }: ImageViewerProps) => {
  const editorRef = useRef<HTMLCanvasElement>(null)
  const [imageLoadingFailed, setImageLoadingFailed] = useState(false)
  const [imageData, setImageData] = useState<ImageData | null>(null)
  const imageOffset = useRef({ x: 0, y: 0 })
  const dragging = useRef(false)
  const dragOrigin = useRef({ x: 0, y: 0 })

  // Whenever the file or canvas size changes we create a new image object
  // that is sized based on the canvas dimensions. After doing this we then
  // trigger a state change to force a re-render which will then actually
  // draw the image to the canvas.
  useEffect(() => {
    const img = new Image()
    img.onload = () => {
      const result = calculateImageDrawDimensions(img, width, height)

      setImageData({
        width: result.width,
        height: result.height,
        canvasWidth: width,
        canvasHeight: height,
        image: img,
        rotation: 0,
        dx: 0,
        dy: 0,
      })
    }
    img.onerror = () => {
      setImageLoadingFailed(true)
    }
    img.src = fileUri
  }, [fileUri, width, height])

  // Main image drawing logic in this effect - we redraw whenever the image data
  // changes, which includes the image itself, the canvas size, and the rotation
  useEffect(() => {
    if (!imageData) return

    // Now we have an image, and the canvas element is sized to match, we can
    // actually draw it to the canvas.
    // Note that when a canvas is resized the content within is reset, so we
    // need to draw the image each time (which is why we've got too effects).

    const canvasContext = editorRef.current?.getContext('2d')
    const imageOrientation =
      imageData.width > imageData.height ? 'landscape' : 'portrait'
    const canvasOrientation =
      imageData.canvasWidth > imageData.canvasHeight ? 'landscape' : 'portrait'

    // Clear the canvas fully so we don't get tiling of multiple images
    // across renders
    canvasContext?.clearRect(
      0,
      0,
      imageData.canvasWidth,
      imageData.canvasHeight
    )

    // When the user is dragging the image around we calculate the new offset
    // based on delta values and draw the image at the new offset. We have to
    // calculate the right offset based on rotation, else dragging the image
    // is 'interesting' as the axis are rotated too.
    if (dragging.current) {
      let dx, dy
      if (imageData.rotation === 0) {
        dx = imageData.dx
        dy = imageData.dy
      } else if (imageData.rotation === 90) {
        dx = imageData.dy
        dy = -imageData.dx
      } else if (imageData.rotation === 180) {
        dx = -imageData.dx
        dy = -imageData.dy
      } else if (imageData.rotation === 270) {
        dx = -imageData.dy
        dy = imageData.dx
      }

      imageOffset.current = {
        x: imageOffset.current.x + dx,
        y: imageOffset.current.y + dy,
      }
    } else if (
      // When the image is smaller in both dimensions than the canvas, we center
      // it within the canvas.
      imageData.width < imageData.canvasWidth &&
      imageData.height < imageData.canvasHeight
    ) {
      imageOffset.current = {
        x: imageOffset.current.x,
        y: imageOffset.current.y,
      }
    } else if (
      // When we're not dragging, this is an initial draw of the image on the
      // canvas. We need to position it based on the orientation of the image
      // versus the orientation of the canvas.
      //
      // Center vertically within the canvas when:
      // - a portrait image in a portrait canvas
      // - a landscape image in a portrait canvas
      // - landscape image in a landscape canvas that is taller than the image height
      (imageOrientation === 'portrait' && canvasOrientation === 'portrait') ||
      (imageOrientation === 'landscape' && canvasOrientation === 'portrait') ||
      imageData.height < imageData.canvasHeight
    ) {
      // New offset is calculated to center the image vertically within the canvas
      imageOffset.current = {
        x: imageOffset.current.x,
        y: (imageData.canvasHeight - imageData.height) / 2,
      }
    }

    // Otherwise we center horizontally within the canvas
    else {
      // New offset is calculated to center the image horizontally within the canvas
      imageOffset.current = {
        x: (imageData.canvasWidth - imageData.width) / 2,
        y: imageOffset.current.y,
      }
    }

    canvasContext?.save()

    canvasContext?.translate(
      imageData.canvasWidth / 2,
      imageData.canvasHeight / 2
    )
    canvasContext?.rotate((imageData.rotation * Math.PI) / 180)
    canvasContext?.translate(
      -imageData.canvasWidth / 2,
      -imageData.canvasHeight / 2
    )

    // When the image is 'sideways' we need to swap the width and height
    // before drawing
    let imageWidth = imageData.width
    let imageHeight = imageData.height

    if (imageData.rotation === 90 || imageData.rotation === 270) {
      imageWidth = imageData.height
      imageHeight = imageData.width
    }

    // Finally, draw the image at it's new location
    canvasContext?.drawImage(
      imageData.image,
      imageOffset.current.x,
      imageOffset.current.y,
      imageWidth,
      imageHeight
    )

    canvasContext?.restore()
  }, [imageData])

  // Click handlers below here

  const handleZoomIn = () => {
    if (!imageData) return

    const newWidth = imageData.width * 1.25
    const newHeight = imageData.height * 1.25

    imageOffset.current = {
      x: imageOffset.current.x * 1.25,
      y: imageOffset.current.y * 1.25,
    }

    setImageData({
      ...imageData,
      width: newWidth,
      height: newHeight,
    })
  }

  const handleZoomOut = () => {
    if (!imageData) return

    const newWidth = imageData.width * 0.75
    const newHeight = imageData.height * 0.75

    imageOffset.current = {
      x: imageOffset.current.x * 0.75,
      y: imageOffset.current.y * 0.75,
    }

    setImageData({
      ...imageData,
      width: newWidth,
      height: newHeight,
    })
  }

  const handleRotate = () => {
    if (!imageData) return

    setImageData({
      ...imageData,
      width: imageData.height,
      height: imageData.width,
      rotation: imageData.rotation < 270 ? imageData.rotation + 90 : 0,
    })
  }

  const handleMouseDown = (e: React.MouseEvent) => {
    dragging.current = true
    dragOrigin.current = { x: e.clientX, y: e.clientY }
  }

  const handleMouseUp = () => {
    dragging.current = false
  }

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!dragging.current) return

    // calculate the distance moved since the last mouse move event (or the mouse
    // down event if this is the first move event)
    const dx = e.clientX - dragOrigin.current.x
    const dy = e.clientY - dragOrigin.current.y

    // update the origin so that the next move event is relative to this one
    dragOrigin.current = { x: e.clientX, y: e.clientY }

    imageData &&
      setImageData({
        ...imageData,
        dx,
        dy,
      })
  }

  return (
    <Paper sx={{ padding: 0 }}>
      {imageLoadingFailed && <LoadFailure />}
      {!imageLoadingFailed && imageData && (
        <Box>
          <canvas
            ref={editorRef}
            width={imageData.canvasWidth}
            height={imageData.canvasHeight}
            style={{
              // colour chosen to match the Chrome PDF viewer background for
              // consistency between the PDF and image viewer for most users
              backgroundColor: '#525659',
            }}
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
            onMouseMove={handleMouseMove}
          />
          <Stack direction={'row'} justifyContent={'center'} mt={1}>
            <ZoomOutIcon
              sx={{ color: darkGrey, cursor: 'pointer' }}
              onClick={handleZoomOut}
            />
            <RotateIcon
              sx={{ color: darkGrey, cursor: 'pointer' }}
              onClick={handleRotate}
            />
            <ZoomInIcon
              sx={{ color: darkGrey, cursor: 'pointer' }}
              onClick={handleZoomIn}
            />
          </Stack>
        </Box>
      )}
    </Paper>
  )
}

/**
 * Component to display when an image fails to load.
 */
const LoadFailure = () => (
  <Stack
    alignItems={'center'}
    justifyContent={'center'}
    sx={{
      height: '200px',
      border: '1px dashed rgba(0,0,0,0.2)',
      borderRadius: '4px',
    }}
  >
    <BrokenImageIcon />
    <Typography>Unable to load document.</Typography>
  </Stack>
)

/**
 * Given an image and a canvas width and height to draw it into, calculate
 * the dimensions that the image should be drawn at to fit within the canvas.
 * @param originalImage
 * @param width
 * @param height
 * @returns
 */
function calculateImageDrawDimensions(originalImage, width, height) {
  const horizontalRatio = width / originalImage.width
  const verticalRatio = height / originalImage.height
  const ratio = Math.min(horizontalRatio, verticalRatio)

  return {
    width: originalImage.width * ratio,
    height: originalImage.height * ratio,
  }
}
